import { Dialog } from "@base-ui/react/dialog";
import { ArrowsClockwiseIcon, DotsThreeOutlineIcon, SquaresFourIcon, TrayIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { q } from "./api.ts";
import { activateWorkspace } from "./workspace-settings.tsx";
import { resetAnalytics, trackAppVisit } from "./analytics.ts";
import { lastProduct } from "./current-product.ts";
import { number } from "./format.ts";
import { locale, setLanguage } from "./i18n.ts";
import { ACTIONS_LABELS, AGENT_PLAYBOOK_LABELS as P, AREA_LABELS as A, DASHBOARD_GRID_LABELS, DASHBOARD_LABELS, INBOX_LABELS as I, LOCAL_LAUNCHER_LABELS, PERSONAL_TOKEN_LABELS, SETTINGS_LABELS, SETTINGS_NAV_LABELS, SHELL_LABELS as L, SIDEBAR_LABELS as S, WEEK_LABELS, WORKSPACE_LABELS as W } from "./labels.ts";
import { hrefOf, titleOf } from "./inbox-items.ts";
import { arrivals, InboxSeenContext, pruneSeen, readSeen, seenToken, writeSeen } from "./inbox-seen.ts";
import { NavigationDialog, type NavigationEntry } from "./navigation.tsx";
import { readPref, writePref } from "./prefs.ts";
import { currentProduct, useCurrentProduct } from "./product-filter.tsx";
import { PRODUCT_PAGE_META, SettingsSidebar, Sidebar } from "./sidebar.tsx";
import { PRODUCT_PAGES, productOfPath } from "./sidebar-items.ts";
import { Callout, ErrorText, Page, ShellContext } from "./ui.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { executionStatus } from "./action-items.ts";
import { CAMPAIGN_VIEW_KEY, readCampaignView } from "./campaign-items.ts";
import { DATA_PAGE_LABELS, DIAGNOSIS_LABELS, LEDGER_LABELS, METRIC_DASHBOARD_LABELS, METRIC_MANAGEMENT_LABELS, METRIC_LABELS, POSTHOG_METRIC_LABELS, PROFILE_DESIGN } from "./labels.ts";

// The control-plane shell (docs/design-system.md §0.2, plan M7 step 7c): the
// sidebar after Linear's beside an inset panel. A product's pages live under
// `/p/:product/`; the overview, inbox, playbooks and settings span the workspace.

interface Jump { key: string; label: string; to: string; productPage?: boolean }

const typing = (target: EventTarget | null) => target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

export function AreaLayout() {
  const navigate = useNavigate();
  const me = useQuery({ ...q.me, retry: false });
  const renderedIdentity = useRef<string | null>(null);
  const identity = me.data ? `${me.data.user.id}:${me.data.workspace.id}` : null;
  const scopeChanged = Boolean(identity && renderedIdentity.current && identity !== renderedIdentity.current);
  useEffect(() => {
    if (scopeChanged) { window.location.replace("/"); return; }
    if (identity) renderedIdentity.current = identity;
  }, [identity, scopeChanged]);
  // Bootstrap the tab's explicit workspace before reading its products.
  const products = useQuery({ ...q.products, enabled: Boolean(me.data) });
  useEffect(() => { if (me.error && (me.error as { status?: number }).status === 401) { resetAnalytics(); navigate({ to: "/sign-in" }); } }, [me.error, navigate]);
  if (scopeChanged || me.isLoading || (me.isSuccess && products.isLoading)) return <Skeleton shape="frame" />;
  if (me.error || products.error) return <ErrorText error={me.error ?? products.error} />;
  if (!me.data) return <Skeleton shape="frame" />;
  return <AreaFrame key={me.data.workspace.id} me={me.data} />;
}

/** The product a jump to a product page goes to: the one on screen, else the one last opened, else the first. */
const productHref = (productId: string | undefined, page: string) => (productId ? `/p/${productId}/${page}` : "/new");

function AreaFrame({ me }: { me: C.Me }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const view: unknown = useRouterState({ select: (state) => (state.location.search as { view?: unknown }).view });
  useEffect(() => {
    // The campaigns' list or timeline in effect at each visit, so their use can be compared (design/plan.md, 功能架构评估第三批); narrow screens only list.
    const layout = /^\/p\/[^/]+\/campaigns$/.test(pathname) && view !== "goals"
      ? window.matchMedia("(max-width: 860px)").matches ? "list" : readCampaignView(readPref(CAMPAIGN_VIEW_KEY))
      : undefined;
    trackAppVisit(me.user.id, pathname, view, layout);
  }, [me.user.id, pathname, view]);
  const actionId = /^\/p\/[^/]+\/actions\/([^/]+)$/.exec(pathname)?.[1];
  const action = useQuery({ ...q.action(actionId ?? ""), enabled: Boolean(actionId) });
  const executionState = action.data ? executionStatus(action.data) : null;
  const executionTarget = action.data?.execution?.claimable || ["stopped", "failed", "expired"].includes(executionState ?? "") ? "action-execution" : "action-execution-summary";
  const inbox = useQuery(q.inbox);
  const { products, product } = useCurrentProduct();
  const count = inbox.data?.items.length ?? 0;
  const notice = useInboxNotice(me, inbox.data?.items, count);
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readPref("sidebar") === "collapsed");
  const [menu, setMenu] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const pid = product?.id;
  const library = useQuery(q.playbookLibrary(pid ?? ""));
  const jumps = useMemo<Jump[]>(() => [
    { key: "o", label: DASHBOARD_LABELS.title, to: "/" },
    { key: "i", label: A.inbox, to: "/inbox" },
    ...PRODUCT_PAGES.map((page) => ({ key: page.jump, label: PRODUCT_PAGE_META[page.key].label, to: productHref(pid, page.key), productPage: true })),
    { key: "l", label: A.playbooks, to: "/playbooks" },
    { key: "s", label: A.settings, to: "/settings/members" },
  ], [pid]);
  const all = useMemo(() => products.data ?? [], [products.data]);
  const entries = useMemo<NavigationEntry[]>(() => [
    // The jump key shows in the menu, as design-system §0.3 asks.
    ...jumps.map((jump) => ({ id: jump.to, label: jump.label, description: `${jump.productPage ? product?.name ?? me.workspace.name : me.workspace.name} · G ${jump.key.toUpperCase()}`, href: jump.to, kind: "page" as const, tone: "gray" })),
    ...(library.data?.playbooks ?? []).map((pb) => ({ id: `use-playbook:${pb.id}`, label: `${P.use.title} · ${pb.title}`, description: product?.name ?? A.playbooks, href: `/playbooks/${encodeURIComponent(pb.id)}?use=true${pid ? `&product=${encodeURIComponent(pid)}` : ""}`, kind: "page" as const })),
    ...(["owner", "admin"].includes(me.workspace.role) ? [{ id: "edit-dashboard", label: DASHBOARD_GRID_LABELS.settings, description: me.workspace.name, href: "/?edit=true", kind: "page" as const }, { id: "add-dashboard-block", label: DASHBOARD_GRID_LABELS.add, description: me.workspace.name, href: "/?add=true", kind: "page" as const }] : []),
    { id: "preferences", label: SETTINGS_LABELS.preferences, description: A.settings, href: "/settings/preferences", kind: "page" as const },
    { id: "workspace-settings", label: W.title, description: me.workspace.name, href: "/settings/workspace", kind: "page" as const },
    { id: "create-workspace", label: W.create, description: A.settings, href: "/settings/workspace?create=true", kind: "workspace" as const },
    { id: "workspace-members", label: SETTINGS_LABELS.members, description: me.workspace.name, href: "/settings/members", kind: "page" as const },
    ...me.workspaces.filter((workspace) => workspace.id !== me.workspace.id).map((workspace) => ({ id: `workspace:${workspace.id}`, label: workspace.name, description: L.switchWorkspace, href: `workspace:${workspace.id}`, kind: "workspace" as const })),
    { id: "tokens", label: SETTINGS_NAV_LABELS.tokens, description: A.settings, href: "/settings/tokens", kind: "page" as const },
    { id: "create-token", label: PERSONAL_TOKEN_LABELS.create, description: SETTINGS_NAV_LABELS.tokens, href: "/settings/tokens#create-token", kind: "workspace" as const },
    ...(action.data?.execution ? [{ id: "execute-action", label: action.data.execution.claimable ? ACTIONS_LABELS.execute : ACTIONS_LABELS.execution, description: product?.name ?? "", href: `${pathname}#${executionTarget}`, kind: "page" as const }] : []),
    ...(pid ? [
      { id: "diagnosis", label: DIAGNOSIS_LABELS.tab, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data`, kind: "page" as const },
      { id: "metrics", label: DATA_PAGE_LABELS.metrics, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?view=metrics`, kind: "page" as const },
      { id: "metric-definitions", label: METRIC_DASHBOARD_LABELS.definitions, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?view=definitions`, kind: "page" as const },
      { id: "product-primary", label: METRIC_MANAGEMENT_LABELS.productPrimary, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?primary=true`, kind: "workspace" as const },
      { id: "metric-sources", label: METRIC_MANAGEMENT_LABELS.sourceMetrics, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?view=sources`, kind: "page" as const },
      { id: "collect-posthog", label: `PostHog · ${POSTHOG_METRIC_LABELS.collect}`, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?view=sources#collect-posthog`, kind: "workspace" as const },
      { id: "lessons", label: PROFILE_DESIGN.tabs.lessons, description: `${product?.name ?? ""} · ${A.profile}`, href: `/p/${pid}/profile?view=lessons`, kind: "page" as const },
      { id: "money", label: LEDGER_LABELS.tab, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?view=money`, kind: "page" as const },
      { id: "add-cost", label: LEDGER_LABELS.addCost, description: `${product?.name ?? ""} · ${LEDGER_LABELS.tab}`, href: `/p/${pid}/data?view=money&add=cost`, kind: "workspace" as const },
      { id: "add-revenue", label: LEDGER_LABELS.addRevenue, description: `${product?.name ?? ""} · ${LEDGER_LABELS.tab}`, href: `/p/${pid}/data?view=money&add=revenue`, kind: "workspace" as const },
      { id: "add-metric", label: METRIC_LABELS.add, description: `${product?.name ?? ""} · ${A.data}`, href: `/p/${pid}/data?create=metric`, kind: "workspace" as const },
      { id: "agent", label: SETTINGS_NAV_LABELS.agent, description: `${product?.name ?? ""} · ${A.settings}`, href: `/settings/products/${pid}/agent`, kind: "page" as const },
      { id: "local-launcher", label: LOCAL_LAUNCHER_LABELS.title, description: `${product?.name ?? ""} · ${SETTINGS_NAV_LABELS.agent}`, href: `/settings/products/${pid}/agent#local-launcher`, kind: "page" as const },
      { id: "grants", label: SETTINGS_NAV_LABELS.grants, description: `${product?.name ?? ""} · ${A.settings}`, href: `/settings/products/${pid}/grants`, kind: "page" as const },
    ] : []),
    ...all.filter((other) => other.id !== pid).map((other) => ({ id: `product:${other.id}`, label: other.name, description: A.campaigns, href: productHref(other.id, "campaigns"), kind: "product" as const, tone: "gray" })),
    ...(inbox.data?.items ?? []).map((item) => ({ id: item.key, label: titleOf(item), description: `${I.kind[item.kind]} · ${item.product.name}`, href: hrefOf(item), kind: "page" as const, tone: "amber" })),
    // Adding a product is an action, not a page.
    ...(me.product_ids === null ? [{ id: "add-product", label: I.addProduct, description: me.workspace.name, href: "/new", kind: "workspace" as const }] : []),
  ], [jumps, all, pid, product?.name, inbox.data, me, library.data, pathname, action.data?.execution, executionTarget]);
  const toggleSidebar = useCallback(() => setCollapsed((value) => { writePref("sidebar", value ? "open" : "collapsed"); return !value; }), []);
  const shell = useMemo(() => ({ collapsed, toggleSidebar }), [collapsed, toggleSidebar]);
  const openMenu = useCallback(() => { opener.current = document.activeElement as HTMLElement; setMenu(true); }, []);
  const go = useCallback((entry: NavigationEntry) => {
    if (entry.id === "create-workspace") { void navigate({ to: "/settings/workspace", search: { create: true } }); return; }
    if (entry.href.startsWith("workspace:")) { void activateWorkspace(entry.href.slice("workspace:".length)).catch(() => {}); return; }
    const [to, hash] = entry.href.split("#");
    void navigate({ to, hash });
  }, [navigate]);

  // The language chosen on the account wins over this device's (plan M6 step 6b).
  const chosen = me.preferences?.locale ?? null;
  useEffect(() => { if (chosen && chosen !== locale()) setLanguage(chosen); }, [chosen]);
  useEffect(() => { setDrawer(false); }, [pathname]);
  // Settings replace the sidebar with their own navigation; "back" returns to the page before them.
  const inSettings = pathname === "/settings" || pathname.startsWith("/settings/");
  const backTo = useRef("/");
  useEffect(() => { if (!inSettings) backTo.current = pathname; }, [inSettings, pathname]);
  useEffect(() => {
    // G then a letter jumps between pages; never while typing (design system §0.3).
    let pendingG = 0;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); openMenu(); return; }
      if ((event.metaKey || event.ctrlKey) && event.key === "\\") { event.preventDefault(); toggleSidebar(); return; }
      if (event.metaKey || event.ctrlKey || event.altKey || typing(event.target) || document.querySelector("[role=dialog]:not([aria-modal=false])")) return;
      if (event.key === "Escape" && inSettings) { event.preventDefault(); void navigate({ to: backTo.current }); return; }
      const key = event.key.toLowerCase();
      if (key === "g") { pendingG = Date.now(); return; }
      if (Date.now() - pendingG < 1200) {
        const jump = jumps.find((candidate) => candidate.key === key);
        pendingG = 0;
        if (jump) { event.preventDefault(); void navigate({ to: jump.to }); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jumps, navigate, openMenu, toggleSidebar, inSettings]);

  const side = inSettings
    ? <SettingsSidebar me={me} products={all} backTo={backTo.current} />
    : <Sidebar me={me} products={all} items={inbox.data?.items} onSearch={openMenu} onHide={drawer ? () => setDrawer(false) : toggleSidebar} />;
  // Narrow screens: the overview, inbox and product week; More opens the sidebar.
  const bar: { to: string; label: string; icon: ComponentType<{ size?: number; weight?: "fill" | "regular" }>; active: boolean }[] = [
    { to: "/", label: DASHBOARD_LABELS.title, icon: SquaresFourIcon, active: pathname === "/" },
    { to: "/inbox", label: A.inbox, icon: TrayIcon, active: pathname === "/inbox" || pathname.startsWith("/inbox/") },
    { to: productHref(pid, "week"), label: WEEK_LABELS.title, icon: ArrowsClockwiseIcon, active: /^\/p\/[^/]+\/week/.test(pathname) },
  ];
  return <ShellContext.Provider value={shell}><InboxSeenContext.Provider value={notice.seen}><Dialog.Root open={drawer} onOpenChange={setDrawer}>
    <div className={`z-app${collapsed ? " is-collapsed" : ""}`}>
      <aside className="z-side" aria-label={L.workspace}>{side}</aside>
      <main className="z-main">
        <Outlet />
      </main>
      <nav className="z-modebar" aria-label={S.tabs}>
        {bar.map((item) => <Link key={item.label} to={item.to} className={`z-mode${item.active ? " is-active" : ""}`} aria-current={item.active ? "page" : undefined}>
          <item.icon size={22} weight={item.active ? "fill" : "regular"} />{item.label}{item.to === "/inbox" && count > 0 && <span className="z-badge">{number(count)}</span>}
        </Link>)}
        <Dialog.Trigger className={`z-mode${drawer ? " is-active" : ""}`} aria-label={L.moreLabel}><DotsThreeOutlineIcon size={22} />{L.more}</Dialog.Trigger>
      </nav>
    </div>
    <Dialog.Portal><Dialog.Backdrop className="z-scrim" /><Dialog.Popup className="z-side z-side-drawer"><Dialog.Title className="z-sr">{L.workspace}</Dialog.Title>{side}</Dialog.Popup></Dialog.Portal>
    <NavigationDialog open={menu} onOpenChange={(open) => { if (!open) setMenu(false); }} entries={entries} finalFocus={() => opener.current?.isConnected ? opener.current : false} onNavigate={go} />
    <p className="z-sr" role="status">{notice.announcement}</p>
  </Dialog.Root></InboxSeenContext.Provider></ShellContext.Provider>;
}

/** `/p/:product/…`: the product must be one of the workspace's; the page below reads it from the address. */
export function ProductLayout() {
  const { productId } = useParams({ strict: false }) as { productId?: string };
  const products = useQuery(q.products);
  if (products.isLoading) return <Page crumbs={[]} wide><Skeleton /></Page>;
  if (products.data && !products.data.some((product) => product.id === productId)) {
    return <Page crumbs={[{ label: A.product }]}><Callout tone="warn"><p>{S.productNotFound}</p><p><Link to="/" className="z-link">{A.inbox}</Link></p></Callout></Page>;
  }
  return <Outlet />;
}

/**
 * Goes to `target` once it is known, replacing the address. A redirect must
 * not follow the address while it navigates, or it would navigate again on
 * every change: hence one navigation, guarded by a ref.
 */
function useRedirect(target: { to: string; params?: Record<string, string>; search?: Record<string, unknown> } | null) {
  const navigate = useNavigate();
  const done = useRef(false);
  const key = target ? JSON.stringify(target) : null;
  useEffect(() => {
    if (!key || done.current) return;
    done.current = true;
    void navigate({ ...(JSON.parse(key) as { to: string }), replace: true });
  }, [key, navigate]);
}

/** An address from before products lived in the path (`/data?product=`, `/home`): the same page of the product it names, else the last one. */
export function ProductPageRedirect({ page }: { page: "week" | "campaigns" | "data" | "profile" }) {
  const initial = useRouterState({ select: (state) => state.location.search as Record<string, unknown> });
  const products = useQuery(q.products);
  const named = typeof initial.product === "string" ? initial.product : null;
  const product = products.data ? currentProduct(products.data, named) : undefined;
  const { product: _named, ...rest } = initial;
  useRedirect(products.isLoading ? null : product ? { to: `/p/$productId/${page}`, params: { productId: product.id }, search: rest } : { to: "/new" });
  return <Page crumbs={[]} wide><Skeleton /></Page>;
}

/** `/campaigns/:id`: the campaign's own product decides where it opens. */
export function CampaignRedirect() {
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  const campaign = useQuery({ ...q.campaign(campaignId ?? ""), enabled: Boolean(campaignId) });
  useRedirect(campaign.data ? { to: "/p/$productId/campaigns/$campaignId", params: { productId: campaign.data.campaign.product_id, campaignId: campaign.data.campaign.id } } : null);
  if (campaign.error) return <Page crumbs={[{ label: A.campaigns }]}><ErrorText error={campaign.error} /></Page>;
  return <Page crumbs={[]} wide><Skeleton /></Page>;
}

/**
 * In-app notice (design/plan.md M1 step 5b): which items this browser has not
 * opened, a polite announcement when items arrive while the app is open, and
 * the count in the tab title. The inbox refetches every minute while visible.
 */
function useInboxNotice(me: C.Me, items: C.InboxItem[] | undefined, count: number) {
  const scope = `${me.workspace.id}:${me.user.id}`;
  const [seen, setSeen] = useState(() => readSeen(scope));
  const [announcement, setAnnouncement] = useState("");
  const previous = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!items) return;
    // The first read is the starting point; only later reads announce arrivals.
    if (previous.current) {
      const arrived = arrivals(previous.current, items);
      if (arrived.length === 1) setAnnouncement(A.arrivedOne(I.kind[arrived[0].kind], arrived[0].product.name));
      else if (arrived.length > 1) setAnnouncement(A.arrived(arrived.length));
    }
    previous.current = new Set(items.map(seenToken));
    setSeen((current) => { const kept = pruneSeen(current, items); if (kept !== current) writeSeen(scope, kept); return kept; });
  }, [items, scope]);
  useEffect(() => {
    document.title = count ? `(${number(count)}) zooi.ai` : "zooi.ai";
    return () => { document.title = "zooi.ai"; };
  }, [count]);
  const value = useMemo(() => ({
    isNew: (item: C.InboxItem) => !seen.has(seenToken(item)),
    markSeen: (item: C.InboxItem) => setSeen((current) => {
      const token = seenToken(item);
      if (current.has(token)) return current;
      const next = new Set(current).add(token);
      writeSeen(scope, next);
      return next;
    }),
  }), [seen, scope]);
  return { seen: value, announcement };
}
